import { useMemo } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PMBadge, PMDataTable, PMEmptyState, PMFilterBar, PMSkeleton, PMTabs } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useReports, type Report } from "@/data/queries";
import { levelDef } from "@/lib/levels";
import { Page } from "@/ui/Page";
import { CountUp } from "@/ui/motion";
import { relTime, shortDate } from "@/ui/format";
import { useState } from "react";

type RStatus = "signed" | "needs" | "failed" | "generating";
function rstatus(r: Report): RStatus {
  if (r.status === "failed") return "failed";
  if (r.approved_at) return "signed";
  if (r.status && r.status !== "completed") return "generating";
  return "needs";
}
const META: Record<RStatus, { label: string; badge: "success" | "primary" | "error" | "warning" }> = {
  signed: { label: "Signed", badge: "success" },
  needs: { label: "Needs signature", badge: "primary" },
  failed: { label: "Failed", badge: "error" },
  generating: { label: "Generating", badge: "warning" },
};
const TABS = [
  { id: "all", label: "All", match: () => true },
  { id: "needs", label: "Needs signature", match: (s: RStatus) => s === "needs" },
  { id: "signed", label: "Signed", match: (s: RStatus) => s === "signed" },
  { id: "failed", label: "Failed", match: (s: RStatus) => s === "failed" },
];

export function ReportsPage() {
  const { teamId } = useParams({ from: "/t/$teamId/reports" });
  const search = useSearch({ from: "/t/$teamId/reports" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const reports = useReports();
  const [q, setQ] = useState("");
  const tab = search.status || "all";

  const counts = useMemo(() => Object.fromEntries(TABS.map((t) => [t.id, reports.data.filter((r) => t.match(rstatus(r))).length])), [reports.data]);
  const weekAgo = Date.now() - 7 * 86_400_000;
  const signedWeek = reports.data.filter((r) => r.approved_at && new Date(r.approved_at).getTime() > weekAgo).length;
  const rows = reports.data.filter((r) => {
    if (!TABS.find((t) => t.id === tab)!.match(rstatus(r))) return false;
    const needle = q.trim().toLowerCase();
    return !needle || `${r.visit_title} ${r.location_label} ${r.config_name}`.toLowerCase().includes(needle);
  });
  const locLevel = hierarchy.levels.at(-1)!;

  return (
    <Page chip="BUSINESS" title="Reports" subtitle={`${reports.data.length} visit reports · ${counts.needs} awaiting signature`}>
      <div className="grid grid-3">
        <Stat emoji="📄" tone="tone-brand" value={reports.data.length} label="Reports" />
        <Stat emoji="✍️" tone="tone-warn" value={counts.needs} label="Awaiting signature" />
        <Stat emoji="✅" tone="tone-text" value={signedWeek} label="Signed this week" />
      </div>
      <PMTabs value={tab} onChange={(id: string) => nav({ to: ".", search: { status: id === "all" ? undefined : id }, replace: true } as any)} tabs={TABS.map((t) => ({ id: t.id, label: t.label, count: counts[t.id] }))} aria-label="Report status" />
      <PMFilterBar search={q} onSearch={setQ} searchPlaceholder={`Search visits, ${locLevel.plural.toLowerCase()}, templates`} />
      {reports.isLoading && !reports.data.length ? (
        <PMSkeleton shape="rect" height={320} radius={12} />
      ) : rows.length === 0 ? (
        <PMEmptyState tone="brand" title={reports.data.length ? "No reports match" : "No reports yet"} description={reports.data.length ? "Try another tab or clear the search." : "Reports are drafted automatically after a visit is completed."} />
      ) : (
        <div className="card">
          <PMDataTable
            rowKey="id"
            rows={rows}
            onRowClick={(r: Report) => r.field_note_id && nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: r.field_note_id }, search: { tab: "report" } })}
            columns={[
              { key: "visit_title", label: "Report", sortable: true, render: (r: Report) => <span><strong>{r.visit_title || "Field visit"}</strong><span className="hint" style={{ display: "block" }}>{r.config_name || "Report"}</span></span> },
              { key: "location_label", label: locLevel.label, sortable: true, render: (r: Report) => <span>{levelDef(locLevel.key, hierarchy.key).emoji} {r.location_label || "—"}</span> },
              { key: "generated_at", label: "Generated", sortable: true, sortValue: (r: Report) => r.generated_at || r.created_at, render: (r: Report) => <span title={relTime(r.generated_at || r.created_at)}>{shortDate(r.generated_at || r.created_at)}</span> },
              { key: "status", label: "Status", sortable: true, sortValue: (r: Report) => META[rstatus(r)].label, render: (r: Report) => <PMBadge variant={META[rstatus(r)].badge} dot uppercase>{META[rstatus(r)].label}</PMBadge> },
              { key: "approver_name", label: "Approver", render: (r: Report) => r.approver_name || <span className="subtle">—</span> },
            ]}
          />
        </div>
      )}
    </Page>
  );
}

function Stat({ emoji, tone, value, label }: { emoji: string; tone: string; value: number; label: string }) {
  return (
    <div className="stat">
      <span className={`stat-ic ${tone}`}>{emoji}</span>
      <span><span className="stat-val"><CountUp value={value} /></span><span className="stat-lbl" style={{ display: "block" }}>{label}</span></span>
    </div>
  );
}
